import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppNavigate } from "../lib/navigate";
import { ArrowLeft, CheckCircle2, Clock3, LoaderCircle, Package } from "lucide-react";
import api from "../lib/api";
import { saveCart } from "../lib/cartStorage";
import { formatDate, formatTime } from "../lib/format";
import { buildCartLinesFromOrder } from "../lib/orderReorder";
import { formatCurrency, normalizeHexColor } from "../lib/productUtils";

const DEFAULT_PRIMARY_COLOR = "#38bdf8";

function LineSummary({ item }) {
  const parts = [];
  if (item?.variantName) {
    parts.push({ label: `Variante: ${item.variantName}`, priceAddon: null });
  }
  if (Array.isArray(item?.selectedOptions)) {
    item.selectedOptions.forEach((option) => {
      parts.push({
        label: `${option?.label || "Opzione"}: ${option?.value || "-"}`,
        priceAddon: Number(option?.priceAddon) || 0,
      });
    });
  }
  if (item?.variableSelection?.value) {
    parts.push({
      label: `Taglio: ${String(item.variableSelection.value)} ${item.variableSelection.unit || ""}`.trim(),
      priceAddon: null,
    });
  }
  if (!parts.length) {
    return <span className="text-xs text-[#64748b]">Articolo standard</span>;
  }
  return (
    <span className="text-xs text-[#64748b]">
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && <span> • </span>}
          {part.label}
          {part.priceAddon !== null && part.priceAddon !== 0 && (
            <span className="ml-1 font-medium text-[#475569]">
              ({part.priceAddon > 0 ? "+" : ""}{formatCurrency(Math.abs(part.priceAddon))})
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

export default function OrderDetailPage() {
  const { tenantId = "default", orderId = "" } = useParams();
  const navigate = useAppNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [allOrderStatuses, setAllOrderStatuses] = useState([]);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [vatMode, setVatMode] = useState("exclusive");

  useEffect(() => {
    let active = true;

    async function loadOrder() {
      setLoading(true);
      setError("");
      try {
        const [orderResponse, configResponse] = await Promise.allSettled([
          api.get(`/webapp/orders/${orderId}`, {
            headers: { "x-tenant-id": tenantId },
          }),
          api.get("/webapp/config", {
            headers: { "x-tenant-id": tenantId },
          }),
        ]);

        if (orderResponse.status !== "fulfilled") {
          throw orderResponse.reason;
        }

        if (!active) return;
        setOrder(orderResponse.value?.data?.data || null);

        if (configResponse.status === "fulfilled") {
          const payload = configResponse.value?.data?.data || {};
          const statuses = payload.orderStatuses || [];
          setAllOrderStatuses(statuses);
          const color = normalizeHexColor(payload.branding?.primaryColor, DEFAULT_PRIMARY_COLOR);
          setPrimaryColor(color);
          setVatMode(payload.vatMode || "exclusive");
        } else {
          setAllOrderStatuses([]);
          setPrimaryColor(DEFAULT_PRIMARY_COLOR);
        }
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.error?.message || "Impossibile caricare il dettaglio ordine.");
        setOrder(null);
        setAllOrderStatuses([]);
        setPrimaryColor(DEFAULT_PRIMARY_COLOR);
      } finally {
        if (active) setLoading(false);
      }
    }

    if (orderId) {
      loadOrder();
    } else {
      setLoading(false);
      setOrder(null);
      setError("Ordine non valido.");
    }

    return () => {
      active = false;
    };
  }, [orderId, tenantId]);

  const items = useMemo(() => (Array.isArray(order?.items) ? order.items : []), [order]);

  const currentStatus = useMemo(
    () => allOrderStatuses.find((s) => String(s._id) === String(order?.statusId)),
    [order?.statusId, allOrderStatuses]
  );

  function handleReorder() {
    if (!order) return;
    const lines = buildCartLinesFromOrder(order);
    saveCart(tenantId, lines);
    navigate(`/${tenantId}`);
  }

  return (
    <div className="min-h-screen bg-[#efefef]">
      <div className="mx-auto min-h-screen w-full max-w-[800px] bg-white shadow-sm">
        <header className="sticky top-0 z-30 border-b border-[#e2e8f0] bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(`/${tenantId}/orders`)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d7e0ea] bg-white text-[#334155]"
              aria-label="Torna allo storico"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="text-base font-semibold text-[#0f172a]">Riepilogo ordine</h1>
            <div className="w-9" />
          </div>
        </header>

        <main className="space-y-5 px-4 pb-32 pt-5">
          {loading && (
            <div className="flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#64748b]">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              Caricamento ordine...
            </div>
          )}

          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          ) : null}

          {!loading && !error && order ? (
            <>
              <div className="flex items-start justify-between gap-4 border-b border-[#e2e8f0] pb-4">
                <div>
                  <p className="text-base font-semibold text-[#0f172a]">Ordine #{order?.orderNumber || order?._id}</p>
                  <p className="mt-1 text-xs text-[#64748b]">{formatDate(order?.createdAt)} alle {formatTime(order?.createdAt)}</p>
                </div>
                <div
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{
                    backgroundColor: currentStatus?.color ? `${currentStatus.color}20` : "#f0f0f0",
                  }}
                >
                  <CheckCircle2
                    className="h-4 w-4"
                    style={{ color: currentStatus?.color || "#cbd5e1" }}
                  />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: currentStatus?.color || "#64748b" }}
                  >
                    {order?.statusName || "-"}
                  </span>
                </div>
              </div>

              {allOrderStatuses.length > 1 && (
                <div className="flex w-full overflow-x-auto -mx-4 px-4 pb-1">
                  {allOrderStatuses.map((status, idx) => {
                    const currentStatusIndex = allOrderStatuses.findIndex((s) => String(s._id) === String(order?.statusId));
                    const isDone = idx <= currentStatusIndex;
                    const statusColor = isDone ? (status?.color || "#cbd5e1") : "#cbd5e1";
                    const Icon = isDone ? CheckCircle2 : Clock3;
                    const leftColor = idx > 0 && idx <= currentStatusIndex
                      ? (allOrderStatuses[idx - 1]?.color || "#e5e7eb")
                      : "#e5e7eb";
                    const rightColor = idx < currentStatusIndex
                      ? (status?.color || "#e5e7eb")
                      : "#e5e7eb";

                    return (
                      <div key={String(status._id)} className="flex flex-col items-center flex-1 min-w-0">
                        {/* Icon row with half-lines */}
                        <div className="flex w-full items-center">
                          <div className="flex flex-1 items-center">
                            {idx > 0 && <div className="h-0.5 w-full" style={{ backgroundColor: leftColor }} />}
                          </div>
                          <div
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                            style={{ backgroundColor: `${statusColor}30` }}
                          >
                            <Icon className="h-5 w-5" style={{ color: statusColor }} />
                          </div>
                          <div className="flex flex-1 items-center">
                            {idx < allOrderStatuses.length - 1 && <div className="h-0.5 w-full" style={{ backgroundColor: rightColor }} />}
                          </div>
                        </div>
                        {/* Label */}
                        <p className="mt-1 w-full text-center text-xs font-medium" style={{ color: statusColor }}>
                          {status.name}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              <section className="overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white">
                {items.map((item, index) => {
                  const imageUrl = item?.productSnapshot?.image || item?.productSnapshot?.images?.[0]?.url || "";
                  return (
                  <div key={`${String(item?.productId || index)}-${index}`} className="border-b border-[#eef2f7] px-3 py-2.5 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item?.productSnapshot?.name || "Prodotto"}
                            className="h-14 w-14 shrink-0 rounded-xl object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.style.display = "none";
                              e.currentTarget.nextElementSibling?.style.setProperty("display", "flex");
                            }}
                          />
                        ) : null}
                        <div
                          className="h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#f1f5f9]"
                          style={{ display: imageUrl ? "none" : "flex" }}
                        >
                          <Package className="h-7 w-7 text-[#cbd5e1]" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#0f172a]">{item?.productSnapshot?.name || "Prodotto"}</p>
                          <p className="mt-0.5"><LineSummary item={item} /></p>
                          {item?.notes ? <p className="mt-0.5 text-xs italic text-[#64748b]">Nota: {item.notes}</p> : null}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#64748b]">Qta {Number(item?.quantity) || 0}</p>
                        <p className="text-sm font-semibold text-[#0f172a]">{formatCurrency(Number(item?.subtotal) || 0)}</p>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </section>

              <section className="overflow-hidden rounded-2xl border border-[#e2e8f0]">
                <div className="space-y-1.5 px-4 py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748b]">Totale parziale:</span>
                    <span className="font-medium text-[#475569]">{formatCurrency(Number(order?.subtotal) || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748b]">{vatMode === "inclusive" ? "di cui IVA:" : "+ IVA:"}</span>
                    <span className="font-medium text-[#475569]">{formatCurrency(Number(order?.vatAmount) || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748b]">Spedizione:</span>
                    <span className="font-medium text-[#475569]">{formatCurrency(Number(order?.shippingCost) || 0)}</span>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-[#e2e8f0] pt-2">
                    <span className="text-sm font-bold uppercase tracking-wide text-[#334155]">TOTALE ORDINE:</span>
                    <span className="text-2xl font-bold" style={{ color: primaryColor }}>{formatCurrency(Number(order?.total) || 0)}</span>
                  </div>
                </div>
              </section>

              {Array.isArray(order?.checkoutAnswers) && order.checkoutAnswers.length > 0 && (
                <section className="overflow-hidden rounded-2xl border border-[#e2e8f0]">
                  <div className="px-4 py-3 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">Informazioni aggiuntive</p>
                    {order.checkoutAnswers.map((answer, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-[#64748b] shrink-0">{answer?.label || "—"}:</span>
                        <span className="text-right font-medium text-[#0f172a]">
                          {Array.isArray(answer?.value) ? answer.value.join(", ") : String(answer?.value ?? "—")}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : null}
        </main>

        {!loading && !error && order ? (
          <div className="pointer-events-none fixed bottom-0 left-1/2 z-30 w-full max-w-[800px] -translate-x-1/2">
            <div className="pointer-events-auto border border-b-0 border-[#e2e8f0] bg-white px-4 pb-5 pt-3 shadow-[0_-3px_12px_rgba(0,0,0,0.08)]">
              <button
                type="button"
                onClick={handleReorder}
                className="flex h-14 w-full items-center justify-between rounded-xl px-5 text-white"
                style={{ backgroundColor: primaryColor }}
              >
                <span className="text-sm font-bold uppercase tracking-wide">RICREA ORDINE</span>
                <span className="text-lg font-bold">{formatCurrency(Number(order?.total) || 0)}</span>
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
